import { useDataFormatter } from "@/composable/data-formatter";
import { useTableOrdering } from "@/composable/table-ordering";
import { wheneverOnce } from "@/composable/whenever-once";
import { mdiDeleteOutline, mdiPencilOutline, mdiPlus } from "@mdi/js";
import { DataDefinition } from "@soft-db/data-formatter";
import { useForm } from "@soft-db/form";
import { SoftButton, SoftIcon, SoftTable, createColumnHelper } from "@soft-db/ui";
import { v4 as uuidv4 } from "uuid";
import DataConfigDialog from "./DataConfigDialog.vue";
import {
	CursorDataOptions,
	CursorValueDataOptions,
	defaultCursorValueDataOptions,
} from "./cursor-value-data-options";

export default defineComponent({
	name: "CursorValueDataConfig",
	props: {
		modelValue: {
			type: Object as PropType<CursorValueDataOptions>,
			default: () => ({ ...defaultCursorValueDataOptions }),
		},
	},
	setup(props, { emit }) {
		const { t } = useI18n();
		const { generateDataLabel } = useDataFormatter();

		const form = useForm<CursorValueDataOptions>({ mode: "default" });

		const dialog = ref(false);

		const valueModel = useVModel(props, "modelValue", emit, { clone: true });

		const dataField = form.useField("data", { initialValue: [] });

		wheneverOnce(
			valueModel,
			(data) => {
				dataField.data.value = data.data;
			},
			{ immediate: true }
		);

		const currentQuery = ref<Partial<CursorDataOptions>>();

		function createNewQuery() {
			currentQuery.value = { id: undefined, query: undefined };
			dialog.value = true;
		}

		function deleteQuery(id: string) {
			const queryIndex = dataField.data.value.findIndex((config) => config.id === id);
			if (queryIndex === -1) return;

			dataField.data.value.splice(queryIndex, 1);
		}

		function saveQuery(query: CursorDataOptions) {
			if (!query.id) {
				query.id = uuidv4();
				dataField.data.value.push(query);
			} else {
				const configIndex = dataField.data.value.findIndex((config) => config.id === query.id);
				if (configIndex === -1) return;
				dataField.data.value.splice(configIndex, 1, query);
			}
			currentQuery.value = undefined;
		}

		const dataColumnsHelper = createColumnHelper<CursorDataOptions>();
		const tableOrdering = useTableOrdering(dataField.data);
		const dataTableColumns = computed(() => [
			tableOrdering,
			dataColumnsHelper.accessor("query", {
				header: t("dashboard.axisData"),
				cell: ({ getValue }) =>
					generateDataLabel({
						...getValue(),
					} as DataDefinition),
				enableSorting: false,
			}),

			dataColumnsHelper.display({
				id: "action",
				cell: ({ row }) => (
					<div class="float-right flex items-center space-x-4">
						<div>
							<SoftButton
								ghost
								onClick={() => {
									currentQuery.value = row.original;
									dialog.value = true;
								}}
							>
								<SoftIcon>{mdiPencilOutline}</SoftIcon>
							</SoftButton>
							<SoftButton ghost color="error" onClick={() => deleteQuery(row.original.id)}>
								<SoftIcon>{mdiDeleteOutline}</SoftIcon>
							</SoftButton>
						</div>
					</div>
				),
			}),
		]);

		function renderConfigDialog() {
			if (!currentQuery.value) return null;
			return (
				<DataConfigDialog
					query={currentQuery.value}
					v-model:dialog={dialog.value}
					onUpdate:query={saveQuery}
				/>
			);
		}

		watch(dialog, () => {
			if (!dialog.value) {
				currentQuery.value = undefined;
			}
		});

		const updateVModel = form.handleSubmit((config) => {
			valueModel.value = config;
		});

		watch(form.values, () => updateVModel(), { deep: true });

		return () => (
			<div class="p-4">
				<SoftTable
					class="mt-4"
					disable-pagination
					columns={dataTableColumns.value}
					data={dataField.data.value}
				/>

				<SoftButton
					color="secondary"
					class="ml-auto mt-4 flex"
					disabled={!form.valid.value}
					onClick={createNewQuery}
				>
					<SoftIcon>{mdiPlus}</SoftIcon>
					{t("dashboard.addData")}
				</SoftButton>
				{renderConfigDialog()}
			</div>
		);
	},
});
